import { useState, useRef, useEffect } from 'react';
import config from '../../utils/config';
import { useAuth } from '../context/wms-context';
import RenderSuggestions from '../components/RenderSuggestion';
import Notification from '../../components/Notification';
import GoBackModal from '../components/goBack/goBackModal';
import { WMSFetch } from '../utils/common-api-calls';
import InvLookupModal from '../components/goBack/invLookupModal';

export default function WMSGoBack() {
    const { currentUser } = useAuth();

    const [open, setOpen] = useState(false);

    const [inventoryNumber, setInventoryNumber] = useState('');
    const [showLocationsSuggestions, setShowLocationsSuggestions] = useState(false);
    const [locations, setLocations] = useState('');
    const [highlightedLocationIndex, setHighlightedLocationIndex] = useState(-1);
    const [notification, setNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState({});
    const [locationId, setLocationId] = useState('');
    const [inventoryData, setInventoryData] = useState([]);

    const [modalKey, setModalKey] = useState(0);

    const inventoryInputRef = useRef(null);

    const [invLookupOpen, setInvLookupOpen] = useState(false);

    const resetState = () => {
        setInventoryNumber('');
        setLocations('');
        setLocationId('');
        setInventoryData([]);
        setHighlightedLocationIndex(-1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!inventoryNumber.trim()) {
                setNotification(true);
                setNotificationMessage({
                    status: 'error',
                    title: `Inventory Number Required`,
                    message: `Please enter an inventory number first.`,
                });
                inventoryInputRef.current?.focus();
                return;
            }
            let selectedLocationId = locationId;
            let selectedLocationName = locations.trim();

            if (!selectedLocationId) {
                const match = currentUser.locations.locations.find(
                    (loc) => loc?.inactive !== 1 && loc?.Name?.toLowerCase() === selectedLocationName.toLowerCase()
                );
                if (match) {
                    selectedLocationId = match.Id;
                    setLocationId(match.Id);
                } else {
                    setNotification(true);
                    setNotificationMessage({
                        status: 'error',
                        title: `Invalid Location`,
                        message: `The location you entered is not valid. Please select a valid location.`,
                    });
                    return;
                }
            }

            if (!selectedLocationName) {
                setNotification(true);
                setNotificationMessage({
                    status: 'error',
                    title: `Location Required`,
                    message: `Please enter a location.`,
                });
                return;
            }

            const formattedInventoryNumber = inventoryNumber.toLowerCase().includes('inv-')
                ? inventoryNumber
                : `INV-${inventoryNumber}`;

            const response = await WMSFetch(
                'wms/go-back/get-inventory-data',
                { inventoryLineName: formattedInventoryNumber, locationId: selectedLocationId },
                currentUser
            );

            if (response?.error) {
                setNotification(true);
                setNotificationMessage({
                    status: 'error',
                    title: `Error Transerring Inventory Line`,
                    message: response.message,
                });
                return;
            }

            setInventoryData(response);
            setModalKey((k) => k + 1);
            setOpen(true);
        } catch (error) {
            setNotification(true);
            setNotificationMessage({
                status: 'error',
                title: `Error Getting inventory Data`,
                message: 'Please check the inventory number and try again',
            });
        }
    };

    const handleLocationsInputChange = (event) => {
        setLocations(event.target.value);
        setShowLocationsSuggestions(true);
    };

    const handleLocationsSuggestionClick = (location) => {
        setShowLocationsSuggestions(false);
        setLocations(location.Name);
        setLocationId(location.Id);
    };

    const filteringLocationsForInput = currentUser.locations.locations.filter(
        (location) => location?.inactive !== 1 && location?.Name?.toLowerCase().includes(locations.toLowerCase())
    );

    const handleKeyDown = (e, highlightedIndex, suggestions, setter, clickHandler) => {
        let newIndex = -1;

        if (e.key === 'ArrowDown' && highlightedIndex < suggestions.length - 1) {
            newIndex = highlightedIndex + 1;
        } else if (e.key === 'ArrowUp' && highlightedIndex > 0) {
            newIndex = highlightedIndex - 1;
        } else if (e.key === 'Enter' && highlightedIndex > -1) {
            clickHandler(suggestions[highlightedIndex]);
        }

        setter(newIndex);
    };

    useEffect(() => {
        if (notification) {
            setTimeout(() => {
                setNotification(false);
            }, 5000);
        }
    }, [notification]);

    return (
        <div className='flex h-[calc(100vh-64px)] items-center justify-center'>
            <GoBackModal
                open={open}
                setOpen={(val) => {
                    setOpen(val);
                }}
                inventoryData={inventoryData}
                locationId={locationId}
                currentUser={currentUser}
                setNotification={setNotification}
                setNotificationMessage={setNotificationMessage}
                setLocations={setLocations}
                setLocationId={setLocationId}
                setInventoryNumber={setInventoryNumber}
                locationName={locations}
                key={modalKey}
            />
            <InvLookupModal
                open={invLookupOpen}
                setOpen={setInvLookupOpen}
                currentUser={currentUser}
                setSelectedInvLine={setInventoryNumber}
                setNotification={setNotification}
                setNotificationMessage={setNotificationMessage}
            />
            <Notification
                showSavedNotification={notification}
                setShowSavedNotification={setNotification}
                title={notificationMessage.title}
                desc={notificationMessage.message}
                status={notificationMessage.status}
                autoClose={true}
            />
            <form onSubmit={handleSubmit} className='flex flex-col items-center'>
                <div className='flex grid-cols-2 text-center gap-7'>
                    <div className='flex flex-col'>
                        <label htmlFor='inventoryNumber' className='mb-2 text-lg'>
                            Inventory Line
                        </label>
                        <input
                            type='text'
                            placeholder='Enter Inventory Number'
                            value={inventoryNumber}
                            onChange={(e) => setInventoryNumber(e.target.value)}
                            className='p-2 border border-gray-300 rounded w-52'
                            autoFocus
                            ref={inventoryInputRef}
                        />
                    </div>
                    <div className='flex flex-col'>
                        <label htmlFor='inventoryLocation' className='mb-2 text-lg'>
                            Inventory Location
                        </label>
                        <input
                            type='text'
                            placeholder='Type New Location'
                            className='p-2 border border-gray-300 rounded w-52'
                            value={locations}
                            onChange={(e) => handleLocationsInputChange(e)}
                            onKeyDown={(e) =>
                                handleKeyDown(
                                    e,
                                    highlightedLocationIndex,
                                    filteringLocationsForInput,
                                    setHighlightedLocationIndex,
                                    handleLocationsSuggestionClick
                                )
                            }
                            onBlur={() => setTimeout(() => setShowLocationsSuggestions(false), 100)}
                        />
                        {showLocationsSuggestions && locations && (
                            <RenderSuggestions
                                suggestions={filteringLocationsForInput}
                                highlightedIndex={highlightedLocationIndex}
                                onClick={(suggestion) => handleLocationsSuggestionClick(suggestion)}
                            />
                        )}
                    </div>
                </div>
                <div className='flex flex-row gap-4 mt-8'>
                    <button
                        type='button'
                        className='px-4 py-2 text-white rounded bg-blue-primary'
                        onClick={() => setInvLookupOpen(true)}
                    >
                        INV Lookup
                    </button>
                    <button type='submit' className='px-4 py-2 text-white rounded bg-blue-primary'>
                        Submit
                    </button>
                </div>
            </form>
        </div>
    );
}
